import { Injectable, inject } from "@angular/core";
import { BackendService } from "./backend-api.service";
import { TenantStore } from "../state/tenant.store";
import { TenantSwitchingStore } from "../state/tenant-switching.store";
import { TenantPublicInfo } from "../models/tenant.model";
import { TokenStorageService } from "./token-storage.service";
import { UserService } from "./user.service";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { ManifestService } from "./manifest.service";
import { UrlCollection } from "../app-routing.module";

@Injectable({
  providedIn: "root"
})
export class TenantService {
  private backendService = inject(BackendService);
  private tenantStore = inject(TenantStore);
  private tenantSwitchingStore = inject(TenantSwitchingStore);
  private tokenStorage = inject(TokenStorageService);
  private userService = inject(UserService);
  private envService = inject(EnvironmentLoaderService);
  private manifestService = inject(ManifestService);

  // Store original token for enhanced CSRF validation during tenant switch
  private originalTokenForSwitch: string | null = null;

  /**
   * Load tenant information from the backend and update the store
   */
  async loadTenantInfo(): Promise<void> {
    this.tenantStore.dispatchLoadTenantRequest();

    try {
      const tenantInfo = await this.backendService.getTenantPublicInfo();

      if (tenantInfo) {
        this.tenantStore.dispatchLoadTenantSuccess(tenantInfo);
        try {
          this.manifestService.setTenant(tenantInfo);
          const manifest = await this.manifestService.generateManifest();
          this.manifestService.updateManifest(manifest);
        } catch (_error) {
          // fallisce silenziosamente
        }
      } else {
        this.tenantStore.dispatchLoadTenantFailure("Failed to load tenant information");
        throw new Error("Failed to load tenant information");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      this.tenantStore.dispatchLoadTenantFailure(errorMessage);
      throw error;
    }
  }

  /**
   * Get the current tenant from the store
   */
  getCurrentTenant(): TenantPublicInfo | null {
    return this.tenantStore.currentTenant;
  }

  /**
   * Get the tenant title from the store
   */
  getTenantTitle(): string | undefined {
    return this.tenantStore.tenantTitle;
  }

  /**
   * Clear tenant error
   */
  clearError(): void {
    this.tenantStore.dispatchClearError();
  }

  /**
   * Load available tenants for tenant switching
   * Only available for admin and super admin users
   */
  async loadAvailableTenants(): Promise<void> {
    // Check if user has permission to switch tenants
    if (!this.userService.canChangeTenant()) {
      this.tenantSwitchingStore.dispatchSetError("Insufficient permissions to switch tenants");
      return;
    }

    this.tenantSwitchingStore.dispatchSetLoading(true);
    this.tenantSwitchingStore.dispatchClearError();

    try {
      const tenants = await this.backendService.getAvailableTenants();

      if (tenants) {
        this.tenantSwitchingStore.dispatchSetAvailableTenants(tenants);
      } else {
        this.tenantSwitchingStore.dispatchSetAvailableTenants([]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load available tenants";
      this.tenantSwitchingStore.dispatchSetError(errorMessage);
      throw error;
    } finally {
      this.tenantSwitchingStore.dispatchSetLoading(false);
    }
  }

  /**
   * Switch to a different tenant
   * @param tenantId The ID of the tenant to switch to
   */
  async switchTenant(tenantId: string): Promise<void> {
    if (!this.userService.canChangeTenant()) {
      throw new Error("Insufficient permissions to switch tenants");
    }

    const targetTenant = this.tenantSwitchingStore.availableTenants.find(t => t.tenantId === tenantId);
    if (!targetTenant) {
      throw new Error("Target tenant not found");
    }

    this.tenantSwitchingStore.dispatchSetSwitching(true);
    this.tenantSwitchingStore.dispatchClearError();

    try {

      // Step 1: Request tenant switch and get temporary token
      const switchResult = await this.backendService.requestTenantSwitch(tenantId);

      if (!switchResult?.token) {
        throw new Error("Failed to get tenant switch token");
      }

      // Step 2: Store the temporary token
      const tempToken = switchResult.token.token;
      const sessionId = switchResult.sessionId;

      if (!tempToken || !sessionId) {
        throw new Error("Failed to get tenant switch token");
      }

      // Step 3: Redirect to target tenant URL with the token
      // Use the same protocol and port as current URL for development compatibility
      const currentUrl = new URL(window.location.href);
      // encode the switchToken and sessionId
      const switchToken = encodeURIComponent(tempToken);
      const sessionIdEncoded = encodeURIComponent(sessionId);
      const targetPath = "/" + UrlCollection.homepage;
      const targetUrl = `${currentUrl.protocol}//${targetTenant.url}${currentUrl.port ? ":" + currentUrl.port : ""}/${targetPath}?switchToken=${switchToken}&sessionId=${sessionIdEncoded}`;
      window.location.href = targetUrl;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to switch tenant";
      this.tenantSwitchingStore.dispatchSetError(errorMessage);
      this.tenantSwitchingStore.dispatchSetSwitching(false);
      // Clear stored original token on error
      this.originalTokenForSwitch = null;
      throw error;
    }
  }

  /**
   * Complete tenant switch process when arriving at new tenant URL
   * Should be called when the page loads with a switchToken parameter
   */
  async completeTenantSwitch(token: string, sessionId: string): Promise<void> {
    try {
      // Complete the tenant switch process with stored sessionId for enhanced CSRF validation
      const completeResult = await this.backendService.completeTenantSwitch(token, sessionId);

      if (completeResult?.jwtPayload) {
        // Update token storage with the new complete token
        const env = this.envService.getEnvConfig();
        const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;
        this.tokenStorage.saveToken(tokenKey, completeResult);

        // Clear switching state and original token
        this.tenantSwitchingStore.dispatchSetSwitching(false);
        this.tenantSwitchingStore.dispatchClearError();
        this.originalTokenForSwitch = null;

        // Reload tenant info
        await this.loadTenantInfo();

        // Note: Navigation and URL cleanup will be handled by the guard
        // No need to reload the page as the token is already updated
      } else {
        throw new Error("Failed to complete tenant switch");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to complete tenant switch";
      this.tenantSwitchingStore.dispatchSetError(errorMessage);
      this.tenantSwitchingStore.dispatchSetSwitching(false);
      // Clear stored original token on error
      this.originalTokenForSwitch = null;
      throw error;
    }
  }

  /**
   * Get the current tenant switching state
   */
  getTenantSwitchingStore(): TenantSwitchingStore {
    return this.tenantSwitchingStore;
  }

  /**
   * Clear tenant switching state
   */
  clearTenantSwitchingState(): void {
    this.tenantSwitchingStore.dispatchClearTenants();
  }


  /**
   * Get the stored original token for enhanced CSRF validation
   * Used by BackendService during tenant switch completion
   */
  getStoredOriginalToken(): string | null {
    return this.originalTokenForSwitch;
  }
} 